import moment from "moment-timezone";
import { useSelector } from "react-redux";
import { makeStyles } from "@mui/styles";
import { MenuItem, Select } from "@mui/material";

import {
  DATED,
  DAY,
  SELECT_DATED_PLAN,
  SELECT_TEMPLATE,
  TEMPLATE,
} from "../../../consts/strings";
import { DATED_PLAN, MENU_ITEM } from "../../../consts/sentryIds";
import { palette } from "../../../theme/palette";

import { getMatrixProfiles } from "../../../reducers/matrixProfile/selectors";
import { partialPostcodesVersionsSelector } from "../../../reducers/depot/selectors";
import {
  findProfile,
  formatDatedPlanName,
  formatTemplateName,
} from "../../../utils/plans";

import { DayChips } from "../../DayChips/DayChips";
import { Text } from "../../Base";
import { View } from "../../View";

const useStyles = makeStyles({
  text: {
    fontSize: "12px",
    fontWeight: "bold",
    marginRight: "4px",
    marginBottom: "10px",
  },
  warningTextContainer: {
    width: "28rem",
  },
  warningText: {
    color: palette.dpdRed,
    fontSize: "14px",
    margin: "10px 0px",
  },
  section: {
    marginTop: "20px",
  },
});

export const SelectPlanModalContent = ({ onPlanSelected, selectedPlan }) => {
  const { dayProfiles, datedProfiles, templates } =
    useSelector(getMatrixProfiles);
  const partialPostcodesVersions = useSelector(
    partialPostcodesVersionsSelector
  );
  const styles = useStyles();

  const selectedPlanPartialPostcodesEffectiveDate =
    selectedPlan &&
    partialPostcodesVersions.find(
      ({ saebiVersion }) =>
        saebiVersion ===
        dayProfiles.find(({ day }) => +day === selectedPlan.value)?.saebiVersion
    )?.effectiveDate;

  return (
    <View>
      <View className={styles.section}>
        <Text className={styles.text}>Available Daily Plans:</Text>
        <DayChips
          disableDaysNotInDepot
          selected={selectedPlan?.type === DAY && selectedPlan.value}
          onDaySelected={(value) => {
            onPlanSelected(value);
          }}
        />
        {selectedPlan?.type === DAY &&
        selectedPlanPartialPostcodesEffectiveDate ? (
          <View className={styles.warningTextContainer}>
            <Text className={styles.warningText}>
              <b>Warning:</b> This daily plan will use the future postcode
              allocation (effective from
              <b>
                {" "}
                {moment(selectedPlanPartialPostcodesEffectiveDate)
                  .tz("Europe/London")
                  .format("DD/MM/YYYY")}
              </b>
              )
            </Text>
          </View>
        ) : null}
      </View>
      <View className={styles.section}>
        <Text className={styles.text}>Available Dated Plans:</Text>
        <Select
          sentryId={DATED_PLAN}
          variant={"outlined"}
          fullWidth
          displayEmpty
          disabled={!datedProfiles || !datedProfiles.length}
          renderValue={(value) =>
            !value
              ? SELECT_DATED_PLAN
              : formatDatedPlanName(
                  findProfile({
                    selectedProfile: { type: DATED, value },
                    dayProfiles,
                    datedProfiles,
                    templates,
                  })
                )
          }
          value={selectedPlan?.type === DATED ? selectedPlan.value : ""}
          onChange={(e) => {
            onPlanSelected({ type: DATED, value: e.target.value });
          }}
        >
          {Array.isArray(datedProfiles) &&
            datedProfiles.map((plan, index) => (
              <MenuItem
                key={index}
                value={plan.templateId}
                sentryId={`${DATED_PLAN}__${MENU_ITEM}`}
              >
                {formatDatedPlanName(plan)}
              </MenuItem>
            ))}
        </Select>
      </View>
      <View className={styles.section}>
        <Text className={styles.text}>Available Template Plans:</Text>
        <Select
          sentryId={TEMPLATE}
          variant={"outlined"}
          fullWidth
          disabled={!templates || !templates.length}
          displayEmpty
          renderValue={(value) =>
            !value
              ? SELECT_TEMPLATE
              : formatTemplateName(
                  findProfile({
                    selectedProfile: { type: TEMPLATE, value },
                    dayProfiles,
                    datedProfiles,
                    templates,
                  })
                )
          }
          value={selectedPlan?.type === TEMPLATE ? selectedPlan.value : ""}
          onChange={(e) => {
            onPlanSelected({ type: TEMPLATE, value: e.target.value });
          }}
        >
          {Array.isArray(templates) &&
            templates.map((plan, index) => (
              <MenuItem
                key={index}
                value={plan.templateId}
                sentryId={`${TEMPLATE}__${MENU_ITEM}`}
              >
                {formatTemplateName(plan)}
              </MenuItem>
            ))}
        </Select>
      </View>
    </View>
  );
};
